import React, { useState } from "react";

export default function FilterDrawer({ isOpen, onClose, searchParams, setSearchParams }) {
  const drawerClasses = isOpen ? "translate-x-0" : "translate-x-full";

  // Expand/collapse states for each category
  const [showMealType, setShowMealType] = useState(false);
  const [showDiet, setShowDiet] = useState(false);
  const [showCuisine, setShowCuisine] = useState(false);
  const [showMethod, setShowMethod] = useState(false);

  // Example arrays
  const CUISINES = [
    "American", "Italian", "Mexican", "Chinese", "Japanese", "Indian", "Mediterranean",
    "French", "Thai", "Greek", "Spanish", "Korean", "Vietnamese", "Middle Eastern", "Peruvian",
    "Caribbean", "African", "German", "Eastern European", "British", "Latin American",
    "Cajun/Creole", "Filipino", "Australian/New Zealand", "Fusion",
  ];
  const DIETS = [
    "vegetarian", "vegan", "gluten-free", "dairy-free", "nut-free", "low-carb",
    "high-protein", "paleo", "Whole30", "low-sodium", "sugar-free", "high-fiber",
    "keto-friendly", "diabetic-friendly", "kosher", "halal",
  ];
  const MEAL_TYPES = [
    "appetizer", "side dish", "soup", "salad", "bread", "sauce/dressing", "drink",
    "condiment", "breakfast", "lunch", "dinner", "snack", "dessert",
  ];
  const METHODS = [
    "baking", "roasting", "grilling", "frying", "boiling", "steaming",
    "sous vide", "slow cooker", "instant pot", "raw", "stovetop"
  ];

  // Helper: get the array of selected values for a given param
  const getSelectedValues = (paramName) => {
    return searchParams.get(paramName)?.split(",").filter(Boolean) || [];
  };

  // Toggle a single value for a given param
  const toggleParam = (paramName, value) => {
    setSearchParams((prev) => {
      const existing = prev.get(paramName)?.split(",") || [];
      if (existing.includes(value)) {
        // Remove it
        const newValues = existing.filter((v) => v !== value);
        if (newValues.length === 0) {
          prev.delete(paramName);
        } else {
          prev.set(paramName, newValues.join(","));
        }
      } else {
        // Add it
        existing.push(value);
        prev.set(paramName, existing.join(","));
      }
      return prev;
    });
  };

  // Clear all filters without closing the drawer
  const clearAllFilters = () => {
    setSearchParams((prev) => {
      prev.delete("mealType");
      prev.delete("diet");
      prev.delete("cuisine");
      prev.delete("cookingMethod");
      return prev;
    });
  };

  // Count how many total filters are selected
  const getSelectedCount = () => {
    let total = 0;
    ["mealType", "diet", "cuisine", "cookingMethod"].forEach((param) => {
      const arr = searchParams.get(param)?.split(",") || [];
      total += arr.filter(Boolean).length;
    });
    return total;
  };
  const selectedCount = getSelectedCount();

  return (
    <div
      className={`
        fixed top-0 right-0 h-full shadow-xl z-50 bg-gray-50
        w-full sm:w-[28rem]
        transform transition-transform duration-700
        ${drawerClasses}
      `}
      onClick={(e) => e.stopPropagation()} // Prevent clicks from closing drawer
    >
      {/* -- Header -- */}
      <div className="flex items-center justify-between p-4 border-b">
        {/* "X" on the left */}
        <button onClick={onClose} className="text-lg text-gray-500 hover:text-gray-700">
          ✕
        </button>

        {/* "Clear X" on the right if any filters are selected */}
        {selectedCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-gray-700 hover:font-semibold transition text-sm"
          >
            Clear
            <span className="ml-2 inline-flex items-center justify-center w-5 h-5 bg-gray-400 text-white rounded-full text-xs leading-none">
              {selectedCount}
            </span>
          </button>
        )}
      </div>

      <div className="p-4 space-y-6 overflow-y-auto h-full pb-24">
        {/* -- Meal Type -- */}
        <FilterCategory
          icon="🍽"
          title="Meal Type"
          paramName="mealType"
          showCategory={showMealType}
          setShowCategory={setShowMealType}
          options={MEAL_TYPES}
          searchParams={searchParams}
          toggleParam={toggleParam}
          getSelectedValues={getSelectedValues}
        />

        {/* -- Diet -- */}
        <FilterCategory
          icon="🥦"
          title="Diet"
          paramName="diet"
          showCategory={showDiet}
          setShowCategory={setShowDiet}
          options={DIETS}
          searchParams={searchParams}
          toggleParam={toggleParam}
          getSelectedValues={getSelectedValues}
        />

        {/* -- Cuisine -- */}
        <FilterCategory
          icon="🌎"
          title="Cuisine"
          paramName="cuisine"
          showCategory={showCuisine}
          setShowCategory={setShowCuisine}
          options={CUISINES}
          searchParams={searchParams}
          toggleParam={toggleParam}
          getSelectedValues={getSelectedValues}
        />

        {/* -- Cooking Method -- */}
        <FilterCategory
          icon="🔥"
          title="Cooking Method"
          paramName="cookingMethod"
          showCategory={showMethod}
          setShowCategory={setShowMethod}
          options={METHODS}
          searchParams={searchParams}
          toggleParam={toggleParam}
          getSelectedValues={getSelectedValues}
        />
      </div>
    </div>
  );
}

/**
 * A reusable sub-component for each filter category (Meal Type, Diet, Cuisine, etc.).
 * Displays selected filters (chips) on the top row, and toggles open/close.
 */
function FilterCategory({
  icon,
  title,
  paramName,
  showCategory,
  setShowCategory,
  options,
  searchParams,
  toggleParam,
  getSelectedValues,
}) {
  const selectedValues = getSelectedValues(paramName);

  // Show at most 2 selected chips, then a "+X" for the rest
  const visibleChips = selectedValues.slice(0, 2);
  const hiddenCount = selectedValues.length > 2 ? selectedValues.length - 2 : 0;

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-3 shadow-sm">
      <button
        className="flex items-center justify-between w-full"
        onClick={() => setShowCategory((prev) => !prev)}
      >
        <div className="flex items-center">
          <span className="mr-2">{icon}</span>
          <h3 className="font-medium">{title}</h3>
        </div>

        <div className="flex items-center space-x-2">
          {/* Render selected chips, if any */}
          {selectedValues.length > 0 && (
            <div className="flex items-center space-x-1 mr-2">
              {visibleChips.map((val) => (
                <span
                  key={val}
                  className="bg-gray-400 text-white text-xs px-2 py-[2px] rounded-full"
                >
                  {val}
                </span>
              ))}
              {hiddenCount > 0 && (
                <span className="bg-gray-400 text-white text-xs px-2 py-[2px] rounded-full">
                  +{hiddenCount}
                </span>
              )}
            </div>
          )}

          {/* +/- toggle */}
          <span className="font-semibold hover:font-bold">
            {showCategory ? "–" : "+"}
          </span>
        </div>
      </button>

      {showCategory && (
        <div className="flex flex-wrap gap-2 mt-3 text-sm">
          {options.map((opt) => {
            const active = selectedValues.includes(opt);
            return (
              <button
                key={opt}
                onClick={() => toggleParam(paramName, opt)}
                className={`
                  px-3 py-1 rounded-full border
                  ${active
                    ? "bg-green-600 text-white border-green-600"
                    : "border-gray-300"
                  }
                `}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
